import { useState, useCallback, useRef } from 'react';
import { checkZipCode } from '../utils/api';
import { debounce, saveZipFlag, getZipFlag } from '../utils/helpers';

interface UseZipCodeCheckReturn {
  zipFlag: boolean | null;
  isChecking: boolean;
  checkZip: (zip: string) => void;
}

/**
 * Hook per verificare i CAP tramite API
 * Gestisce il debouncing e la persistenza in sessionStorage
 */
export function useZipCodeCheck(): UseZipCodeCheckReturn {
  const [zipFlag, setZipFlag] = useState<boolean | null>(() => getZipFlag());
  const [isChecking, setIsChecking] = useState(false);

  // Debounced check function
  const debouncedCheck = useRef(
    debounce(async (zip: string) => {
      if (!zip || zip.length < 3) {
        setZipFlag(null);
        saveZipFlag(null);
        setIsChecking(false);
        return;
      }

      setIsChecking(true);

      try {
        const result = await checkZipCode(zip);
        setZipFlag(result);
        saveZipFlag(result);

        // Dispatch custom event for other components
        document.dispatchEvent(
          new CustomEvent('enpal:zipChecked', {
            detail: { zip, inList: result },
          })
        );
      } catch (error) {
        console.error('Error checking ZIP:', error);
        setZipFlag(null);
        saveZipFlag(null);
      } finally {
        setIsChecking(false);
      }
    }, 350)
  ).current;

  const checkZip = useCallback(
    (zip: string) => {
      debouncedCheck(zip);
    },
    [debouncedCheck]
  );

  return {
    zipFlag,
    isChecking,
    checkZip,
  };
}

